import { Auth0Provider } from '@auth0/auth0-react'
import { queryClient } from '@doneverse/data/api'
import { QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import { syncFlagsFromUrl } from './lib/feature-flags'
import { router } from './router'
import { AuthBootstrapBoundary } from '@/components/auth/AuthBootstrapBoundary'
import { CallHost } from '@/components/calls/CallHost'
import { IncomingCallHost } from '@/components/calls/IncomingCallHost'
import { FeatureFlagBanner } from '@/components/layout/FeatureFlagBanner'
import { Toaster } from '@/components/ui/sonner'
import { TooltipProvider } from '@/components/ui/tooltip'
import { AuthProvider, useAuth } from '@/contexts/AuthContext'
import { E2EAuthProvider } from '@/contexts/E2EAuthProvider'
import { WorkspaceBootstrap } from '@/contexts/WorkspaceContext'
import { useHeartbeat } from '@/hooks/useHeartbeat'
import { useNotificationSubscription } from '@/hooks/useNotificationSubscription'

// Capture ?flags=... from URL before React mounts
syncFlagsFromUrl()

const isE2E = import.meta.env.VITE_E2E_TEST_MODE === 'true'
const E2E_REALTIME_STORAGE_KEY = 'doneverse.e2e.realtime'

function InnerApp() {
  const auth = useAuth()

  return (
    <AuthBootstrapBoundary auth={auth}>
      <RouterProvider router={router} context={{ queryClient, auth }} />
    </AuthBootstrapBoundary>
  )
}

function AppShell({ children }: { children: React.ReactNode }) {
  const { loading, profile, getAccessToken } = useAuth()
  const realtimeEnabled = !isE2E || localStorage.getItem(E2E_REALTIME_STORAGE_KEY) === 'true'

  useHeartbeat({
    enabled: !isE2E && !loading && !!profile?.id,
  })

  useNotificationSubscription({
    userId: profile?.id,
    getToken: getAccessToken,
    enabled: realtimeEnabled && !loading && !!profile?.id,
  })

  return (
    <TooltipProvider>
      <Toaster />
      {/* A human dev aid for ?flags= previews. Hidden under e2e, where the fixed
          top-centre pill would otherwise overlap (and intercept clicks on)
          top-anchored app chrome like the coaching session's Leave button. */}
      {!isE2E && <FeatureFlagBanner />}
      {/* Outside the router on purpose: a call has to survive navigating
          between chats and pages, and an incoming call has to reach the user
          on any page. Both render null unless a call is live. */}
      <CallHost />
      <IncomingCallHost />
      {children}
    </TooltipProvider>
  )
}

const App = () => {
  if (isE2E) {
    return (
      <QueryClientProvider client={queryClient}>
        <E2EAuthProvider>
          <AppShell>
            <WorkspaceBootstrap />
            <InnerApp />
          </AppShell>
        </E2EAuthProvider>
      </QueryClientProvider>
    )
  }

  return (
    <Auth0Provider
      domain={import.meta.env.VITE_AUTH0_DOMAIN}
      clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
      cacheLocation="localstorage"
      useRefreshTokens
      authorizationParams={{
        redirect_uri: `${window.location.origin}/callback`,
        audience: import.meta.env.VITE_AUTH0_AUDIENCE,
        scope: 'openid profile email offline_access',
      }}
    >
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <AppShell>
            <WorkspaceBootstrap />
            <InnerApp />
          </AppShell>
        </AuthProvider>
      </QueryClientProvider>
    </Auth0Provider>
  )
}

export default App
